import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import useSWR, { mutate } from "swr"
import useSWRMutation from "swr/mutation"
import { NotificationSettingsDraft } from "./slice"

export const invalidateNotificationPreferences = () =>
    mutate((key) => Array.isArray(key) && key[0] === "get-notification-preferences")

const getNotificationPreferences = ({ orgId }: { orgId: string }) => {
    return fetchApiGateway("get /org/:orgId/notification-preferences", {
        params: { orgId },
    })
}

export const useNotificationPreferences = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWR(["get-notification-preferences", orgId], () =>
        orgId ? getNotificationPreferences({ orgId }) : undefined
    )

    return { preferences: data, isLoading }
}

const updateNotificationPreferences = ({
    orgId,
    draft,
}: {
    orgId: string
    draft: NotificationSettingsDraft
}) => {
    return fetchApiGateway("put /org/:orgId/notification-preferences", {
        params: { orgId },
        body: {
            entries: draft.entries,
            silence: draft.silence,
            timezoneOffsetMinutes: new Date().getTimezoneOffset(),
        },
    })
}

export const useUpdateNotificationPreferences = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-notification-preferences",
        (key, { arg: draft }: { arg: NotificationSettingsDraft }) =>
            orgId ? updateNotificationPreferences({ orgId, draft }) : undefined,
        {
            onSuccess: (saved) => {
                if (saved) mutate(["get-notification-preferences", orgId], saved, false)
                return invalidateNotificationPreferences()
            },
        }
    )

    return { updateNotificationPreferences: trigger, isMutating }
}
